<template>
    <CampaignBanner
        ref="campaignBanner"
        :key="campaignBannerKey"
        :master-template-height="displayDimensions.height"
        :master-template-width="displayDimensions.width"
        :max-height="maxHeight"
        :max-width="maxWidth"
        :scaling-factor="scalingFactor"
        :resize-mode="resizeMode"
        @toggleResizeMode="onToggleResizeMode"
        @updateInternalScalingFactor="internalScalingFactor = $event"
    >
        <template #bannerContainer>
            <template v-if="isSelectable">
                <Checkbox class="campaign-banner__checkbox" :label="masterTemplate._id">
                    <BannerPreview
                        :id="bannerId"
                        :preview-values="previewValues"
                        lazy-load
                        :scaling-factor="internalScalingFactor"
                        :ad-type="masterTemplate.adType"
                        :playback-namespace="playbackNamespace"
                    />
                </Checkbox>
            </template>

            <template v-else>
                <BannerPreview
                    :id="bannerId"
                    :preview-values="previewValues"
                    lazy-load
                    :scaling-factor="internalScalingFactor"
                    :ad-type="masterTemplate.adType"
                    :playback-namespace="playbackNamespace"
                />
            </template>
        </template>

        <template #bannerFooter>
            <BannerFooterName :is-dark-theme="false" :banner-name="bannerName" />
            <BannerFooterControls :banner-id="bannerId" :playback-namespace="playbackNamespace" />
            <MasterTemplateBannerFooterOptions
                :resize-mode="resizeMode"
                :master-template="masterTemplate"
                :options-visible="optionsVisible"
                @updateOptionsVisible="optionsVisible = $event"
                @toggleResizeMode="onToggleResizeMode"
                @remove="$emit('remove')"
            />
        </template>
    </CampaignBanner>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { MasterTemplate } from "shared-types";
import { TemplateType } from "shared-utils/enums/masterTemplate";

import BannerPreview from "@/components/Previews/BannerPreview.vue";
import CampaignBanner from "@/components/Campaign/CampaignBanner.vue";
import MasterTemplateBannerFooterOptions from "@/components/Previews/BannerFooter/MasterTemplateBannerFooterOptions.vue";
import BannerFooterName from "@/components/Previews/BannerFooter/BannerFooterName.vue";
import BannerFooterControls from "@/components/Previews/BannerFooter/BannerFooterControls.vue";

import { BannerNamespace } from "@/components/PlaybackControls/usePlaybackController";
import { getBannerIdFromMasterTemplate } from "@/utils/getBannerIframeId";
import { formatTemplateSizeLabel, getTemplateDisplayDimensions } from "@/utils";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

export default defineComponent({
    name: "MasterTemplatePreview",
    components: {
        CampaignBanner,
        BannerPreview,
        MasterTemplateBannerFooterOptions,
        BannerFooterName,
        BannerFooterControls
    },

    props: {
        masterTemplate: {
            type: Object as PropType<MasterTemplate>,
            required: true
        },
        isSelectable: {
            type: Boolean,
            default: false
        },
        maxWidth: {
            type: Number
        },
        maxHeight: {
            type: Number
        },
        scalingFactor: {
            type: Number,
            default: 0 // auto scaling
        }
    },

    emits: ["remove", "resizeToggle"],

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            resizeMode: false,
            playbackNamespace: BannerNamespace.MasterTemplates,
            optionsVisible: false,
            internalScalingFactor: 1
        };
    },

    computed: {
        campaignBannerKey() {
            return `${this.masterTemplate._id}-${this.masterTemplate.modified}`;
        },

        previewValues() {
            return JSON.stringify({
                computedOverwrites: [],
                metadata: [],
                masterTemplate: {
                    _id: this.masterTemplate._id,
                    persistentKey: this.masterTemplate.persistentKey,
                    width: this.masterTemplate.width,
                    height: this.masterTemplate.height,
                    ...(this.masterTemplate.adType === TemplateType.AE && { aeInfo: this.masterTemplate.aeInfo }),
                    ...(this.masterTemplate.adType === TemplateType.INDESIGN && {
                        sourceDocuments: this.masterTemplate.sourceDocuments,
                        defaultSharedOverrides: this.masterTemplate.defaultSharedOverrides
                    }),

                    reportingLabel: this.masterTemplate.reportingLabel
                },
                previewUrl: this.masterTemplate.preview,
                ...(this.masterTemplate.adType === TemplateType.PSD && {
                    psdThumbnail: this.masterTemplate.psdThumbnail
                }),
                modified: this.masterTemplate.modified
            });
        },

        bannerName() {
            return `${formatTemplateSizeLabel(this.masterTemplate)} - ${this.masterTemplate.name}`;
        },

        displayDimensions() {
            return getTemplateDisplayDimensions(this.masterTemplate);
        },

        bannerId() {
            return getBannerIdFromMasterTemplate(this.masterTemplate, this.playbackNamespace);
        }
    },

    methods: {
        onToggleResizeMode(resizeMode: boolean) {
            this.resizeMode = resizeMode;
            this.optionsVisible = false;
            this.ga4.trackSegmentInteraction({
                segmentId: this.bannerId,
                segmentName: GA4.SEGMENT_NAMES.MASTER_TEMPLATES_SCALING,
                interactionType: GA4.SEGMENT_INTERACTIONS.CLICK,
                segmentCategory: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES
            });
            this.$emit("resizeToggle", this.resizeMode);
        }
    }
});
</script>
